import React, { useState, useEffect } from 'react';
import { 
  MapPin, Fingerprint, Calendar, 
  ArrowRight, CheckCircle2, 
  ShieldCheck, Loader2, AlertCircle, 
  ArrowUpRight, Monitor, Clock
} from 'lucide-react';
import { attendanceService } from '../../../services/api';
import SignatureCanvas from '../../../components/SignatureCanvas/SignatureCanvas';
import './AttendancePage.css';

interface AttendanceLog {
  id: string;
  type: 'in' | 'out';
  time: string;
  date: string;
  location: string;
  signature: string;
  timestamp: string;
}

interface AttendanceStats {
  total_hours: number;
  weekly_average_hours?: number;
  work_sessions: number;
  work_days: number;
  lastLog: {
    type: 'in' | 'out';
    timestamp: string;
    date: string;
    time: string;
  } | null;
}

const AttendancePage: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [status, setStatus] = useState<'in' | 'out'>('out');
  const [todayLogs, setTodayLogs] = useState<AttendanceLog[]>([]);
  const [showSignature, setShowSignature] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<AttendanceStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Carregar status atual
      const statusResponse = await attendanceService.getStatus();
      if (statusResponse.success && statusResponse.data) {
        setStatus(statusResponse.data.currentStatus);
      }

      // Carregar registros de hoje do usuário logado (para o card "Registros de hoje")
      // O backend já filtra por usuário quando não é admin
      const today = new Date().toISOString().split('T')[0];
      const todayLogsResponse = await attendanceService.getLogs({ date: today });
      if (todayLogsResponse.success) {
        setTodayLogs(todayLogsResponse.data || []);
      }

      // Carregar estatísticas
      const statsResponse = await attendanceService.getStats();
      if (statsResponse.success && statsResponse.data) {
        setStats(statsResponse.data);
      }
    } catch (err: any) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.message || 'Erro ao carregar dados do ponto eletrônico');
    } finally {
      setLoading(false);
    }
  };

  const handleSignatureSave = async (signatureData: string) => {
    try {
      setIsProcessing(true);
      setError(null);

      // Verificar se o navegador suporta geolocalização
      if (!navigator.geolocation) {
        setShowSignature(false);
        setError('Geolocalização não é suportada pelo seu navegador. Não é possível registrar o ponto sem localização.');
        return;
      }

      // Obter localização (obrigatório)
      let location: string;
      let latitude: number;
      let longitude: number;

      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            reject,
            {
              timeout: 10000,
              enableHighAccuracy: true,
              maximumAge: 0
            }
          );
        });

        latitude = position.coords.latitude;
        longitude = position.coords.longitude;

        // Validar se as coordenadas são números válidos
        if (typeof latitude !== 'number' || typeof longitude !== 'number' || 
            isNaN(latitude) || isNaN(longitude)) {
          throw new Error('Coordenadas de localização inválidas');
        }

        location = `Lat: ${latitude.toFixed(4)}, Lng: ${longitude.toFixed(4)}`;
      } catch (geoError: any) {
        setShowSignature(false);
        
        // Mensagens de erro específicas baseadas no tipo de erro
        let errorMessage = 'Não foi possível obter sua localização. ';
        
        if (geoError.code === 1) {
          errorMessage += 'Você negou o acesso à localização. É necessário autorizar o uso da localização para registrar o ponto.';
        } else if (geoError.code === 2) {
          errorMessage += 'A localização não está disponível. Verifique se o GPS está ativado.';
        } else if (geoError.code === 3) {
          errorMessage += 'Tempo esgotado ao obter localização. Tente novamente.';
        } else {
          errorMessage += 'Erro ao acessar o serviço de geolocalização.';
        }
        
        setError(errorMessage);
        return;
      }

      // Fechar o canvas de assinatura apenas após validar a localização
      setShowSignature(false);

      const nextType = status === 'out' ? 'in' : 'out';

      const response = await attendanceService.createLog({
        type: nextType,
        location,
        latitude,
        longitude,
        signature: signatureData,
        notes: null
      });

      if (response.success) {
        // Recarregar dados
        await loadData();
      } else {
        setError(response.message || 'Erro ao registrar ponto');
      }
    } catch (err: any) {
      console.error('Erro ao salvar assinatura:', err);
      setShowSignature(false);
      setError(err.response?.data?.message || 'Erro ao registrar ponto');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatHours = (hours: number): string => {
    const totalMinutes = Math.round(Math.abs(hours) * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m`;
  };

  if (loading) {
    return (
      <div className="attendance-page-loading">
        <Loader2 className="animate-spin" size={48} />
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="attendance-page">
      {error && (
        <div className="attendance-error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Header Section */}
      <div className="attendance-header">
        <div>
          <h1 className="attendance-title">
            Ponto <span className="attendance-title-accent">Eletrônico</span>
          </h1>
          <p className="attendance-subtitle">Gestão de Jornada e Frequência Enterprise</p>
        </div>
        <div className="attendance-status-badge">
          <div className="attendance-status-icon">
            <Monitor size={20} />
          </div>
          <div>
            <p className="attendance-status-label">Status Atual</p>
            <p className={`attendance-status-value ${status === 'in' ? 'status-in' : 'status-out'}`}>
              {status === 'in' ? 'Trabalhando' : 'Fora de Serviço'}
            </p>
          </div>
        </div>
      </div>

      <div className="attendance-grid">
        {/* Main Check-in Area */}
        <div className="attendance-main">
          <div className="attendance-card">
            <div className="attendance-card-decoration" />
            
            <div className="attendance-card-content">
              <div className="attendance-icon-wrapper">
                <Fingerprint size={40} />
              </div>
              
              <div className="attendance-time-section">
                <h2 className="attendance-time">
                  {currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </h2>
                <p className="attendance-date">
                  {currentTime.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                </p>
              </div>

              <div className="attendance-info-badges">
                <div className="attendance-info-badge">
                  <MapPin size={14} />
                  <span>Matriz - Pelotas, RS</span>
                </div>
                <div className="attendance-info-badge">
                  <ShieldCheck size={14} />
                  <span>Registro com Assinatura Digital</span>
                </div>
              </div>

              {isProcessing ? (
                <div className="attendance-processing">
                  <Loader2 className="animate-spin" size={24} />
                  <span>Gravando registro...</span>
                </div>
              ) : !showSignature ? (
                <button 
                  onClick={() => setShowSignature(true)}
                  className={`attendance-action-btn ${status === 'out' ? 'btn-in' : 'btn-out'}`}
                >
                  {status === 'out' ? 'Iniciar Jornada' : 'Finalizar Jornada'}
                </button>
              ) : (
                <div className="attendance-signature-wrapper">
                  <div className="attendance-signature-header">
                    <h3>Confirmação Biométrica</h3>
                    <button 
                      onClick={() => setShowSignature(false)} 
                      className="attendance-signature-cancel"
                    >
                      Cancelar
                    </button>
                  </div>
                  <SignatureCanvas 
                    onSave={handleSignatureSave}
                    onCancel={() => setShowSignature(false)}
                  />
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Info Column */}
        <div className="attendance-sidebar">
          <div className="attendance-stats-card">
            <h3 className="attendance-stats-title">Minha Jornada</h3>
            <div className="attendance-stats-content">
              <div>
                <p className="attendance-stats-hours">
                  {stats ? formatHours(parseFloat(stats.total_hours?.toString() || '0')) : '00h 00m'}
                </p>
                <p className="attendance-stats-label">Horas no mês</p>
              </div>
              <div className="attendance-stats-details">
                <div className="attendance-stats-row">
                  <span>Média Semanal</span>
                  <span>
                    {stats ? formatHours(parseFloat(stats.weekly_average_hours?.toString() || '0')) : '00h 00m'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Registros de Hoje Card */}
          <div className="attendance-history-card">
            <div className="attendance-history-header">
              <h3 className="attendance-history-title">
                <Clock size={16} />
                Registros de Hoje
              </h3>
            </div>

            {todayLogs.length === 0 ? (
              <div className="attendance-empty">
                <Calendar size={48} />
                <p>Nenhum registro encontrado hoje</p>
              </div>
            ) : (
              <div className="attendance-logs">
                {todayLogs.map((log) => (
                  <div key={log.id} className="attendance-log-item">
                    <div className="attendance-log-main">
                      <div className={`attendance-log-icon ${log.type === 'in' ? 'icon-in' : 'icon-out'}`}>
                        {log.type === 'in' ? <ArrowUpRight size={18} /> : <ArrowRight size={18} className="rotated" />}
                      </div>
                      <div>
                        <p className="attendance-log-type">
                          Ponto: {log.type === 'in' ? 'Entrada' : 'Saída'}
                        </p>
                        <p className="attendance-log-time">{log.date} • {log.time}</p>
                      </div>
                    </div>
                    <div className="attendance-log-actions">
                      <CheckCircle2 size={18} className="attendance-log-check" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="attendance-legal-card">
            <div className="attendance-legal-header">
              <AlertCircle size={18} />
              <h3>Lembretes Legais</h3>
            </div>
            <ul className="attendance-legal-list">
              <li>
                <div className="attendance-legal-dot" />
                <p>O registro deve ser feito exclusivamente pelo colaborador.</p>
              </li>
              <li>
                <div className="attendance-legal-dot" />
                <p>Em caso de erro no registro, informe o RH via chamado técnico.</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendancePage;
